import { Component } from '@theme/component';
import { fetchConfig } from '@theme/utilities';
import { CartUpdateEvent, CartErrorEvent } from '@theme/events';

class ProductCalculatorComponent extends Component {
  /** @type {{ form?: HTMLFormElement, result?: HTMLElement }} */
  refs;

  BAG_WEIGHT_KG;
  BAG_WEIGHTS_ARRAY; // Array of { weightKg: number, variantId: number } objects
  // Seeding rates in kg per 1000 m² (provided via attributes)
  // Note: If g/m² attributes are provided, they are numerically equal to kg per 1000 m²
  NEW_LAWN_KG_PER_1000_SQM;
  OVERSEED_KG_PER_1000_SQM;
  bagBreakdown = []; // Store the calculated bag breakdown

  unitToSqm = {
    sqm: 1,
    hectare: 10000,
    acre: 4046.8564224,
    sqyard: 0.83612736,
    sqfeet: 0.09290304,
  };

  connectedCallback() {
    super.connectedCallback();
    // Allow overriding defaults via element attributes from Liquid
    const bagWeightAttr = this.getAttribute('bag-weight-gram');
    // Prefer g/m² attributes if provided, fallback to kg per 1000 m²
    const newRateGPerSqmAttr = this.getAttribute('new-lawn-g-per-sqm');
    const overseedRateGPerSqmAttr = this.getAttribute('overseed-g-per-sqm');
    const newRateAttr = this.getAttribute('new-lawn-kg-per-1000-sqm');
    const overseedRateAttr = this.getAttribute('overseed-kg-per-1000-sqm');
    this.productTitle = this.getAttribute('product-title');
    
    // Parse comma-delimited list of "weight|variantId" pairs into an array
    // Example: "1000|123,2000|456,3000|789"
    if (bagWeightAttr) {
      this.BAG_WEIGHTS_ARRAY = bagWeightAttr
        .split(',')
        .map(item => {
          const [weight, variantId] = item.trim().split('|');
          const weightGrams = parseFloat(weight);
          const variantIdNum = parseInt(variantId, 10);
          
          if (Number.isFinite(weightGrams) && weightGrams > 0 && Number.isFinite(variantIdNum)) {
            return {
              weightKg: weightGrams / 1000, // Convert grams to kg
              variantId: variantIdNum
            };
          }
          return null;
        })
        .filter(item => item !== null); // Remove invalid entries
      
      // Set first bag weight for backward compatibility
      if (this.BAG_WEIGHTS_ARRAY.length > 0) {
        this.BAG_WEIGHT_KG = this.BAG_WEIGHTS_ARRAY[0].weightKg;
      }
    }

    console.log(bagWeightAttr);
    
    // If grams/m² provided, they are numerically equal to kg/1000m²
    const newRateNum = Number(newRateGPerSqmAttr ?? newRateAttr);
    const overseedRateNum = Number(overseedRateGPerSqmAttr ?? overseedRateAttr);
    
    // Get add to cart label from button or use default
    const addToCartButtonSpan = this.querySelector('[data-calculator-add-to-cart] span');
    this.addToCartLabel = addToCartButtonSpan?.textContent || 'Add to Cart';
    
    if (Number.isFinite(newRateNum) && newRateNum > 0) this.NEW_LAWN_KG_PER_1000_SQM = newRateNum;
    if (Number.isFinite(overseedRateNum) && overseedRateNum > 0) this.OVERSEED_KG_PER_1000_SQM = overseedRateNum;

    const { form } = this.refs;
    if (form && !form.dataset.calculatorInit) {
      form.addEventListener('submit', this.#onSubmit);
      form.dataset.calculatorInit = 'true';
    }

    // Add event listener for add to cart button
    const addToCartButton = this.querySelector('[data-calculator-add-to-cart]');
    if (addToCartButton) {
      addToCartButton.addEventListener('click', this.#handleAddToCart.bind(this));
    }
  }

  disconnectedCallback() {
    const { form } = this.refs;
    if (form) form.removeEventListener('submit', this.#onSubmit);
    super.disconnectedCallback();
  }

  #formatNumber(value) {
    try {
      return new Intl.NumberFormat(undefined, { maximumFractionDigits: 2 }).format(value);
    } catch (_) {
      return String(Math.round(value * 100) / 100);
    }
  }

  #onSubmit = (event) => {
    event.preventDefault();
    const { form, result, resultCalculation, resultAddtocart, resultError } = this.refs;
    if (!form || !result || !resultCalculation || !resultAddtocart || !resultError) return;

    const areaInput = form.querySelector('#area-total');
    const unitSelect = form.querySelector('#area-unit');
    const lawnType = form.querySelector('#lawn-type');

    const areaVal = parseFloat(areaInput?.value || '');
    const unit = unitSelect?.value || 'sqm';
    const type = lawnType?.value || 'new';

    if (!Number.isFinite(areaVal) || areaVal <= 0) {
      result.classList.add('hidden');
      resultError.classList.remove('hidden');
      resultError.innerHTML = '<div class="p-4 border text-red-600 text-sm">Please enter a valid area greater than 0.</div>';
      return;
    }

    // Validate config from Liquid
    if (!this.BAG_WEIGHTS_ARRAY || this.BAG_WEIGHTS_ARRAY.length === 0 ||
        !Number.isFinite(this.NEW_LAWN_KG_PER_1000_SQM) || this.NEW_LAWN_KG_PER_1000_SQM <= 0 ||
        !Number.isFinite(this.OVERSEED_KG_PER_1000_SQM) || this.OVERSEED_KG_PER_1000_SQM <= 0) {
          result.classList.add('hidden');
          resultError.classList.remove('hidden');
          resultError.innerHTML = '<div class="p-4 border text-red-600 text-sm">Calculator is not configured. Missing or invalid attributes from Liquid.</div>';
      return;
    }

    const sqmPerUnit = this.unitToSqm[unit] ?? 1;
    const areaSqm = areaVal * sqmPerUnit;

    // Calculate total kg needed based on seeding rate
    const kgPer1000 = type === 'new' ? this.NEW_LAWN_KG_PER_1000_SQM : this.OVERSEED_KG_PER_1000_SQM;
    const totalKgNeeded = (areaSqm * kgPer1000) / 1000;

    // Find minimum number of bags using available bag weights
    // Sort bags by weight (descending) to use larger bags first for efficiency
    const sortedBags = [...this.BAG_WEIGHTS_ARRAY].sort((a, b) => b.weightKg - a.weightKg);
    
    let remainingKg = totalKgNeeded;
    const bagBreakdown = [];
    
    // Greedy algorithm: use largest bags first to minimize total count
    for (const bag of sortedBags) {
      if (remainingKg <= 0) break;
      
      // Calculate how many bags of this size we need
      const bagsOfThisSize = Math.floor(remainingKg / bag.weightKg);
      if (bagsOfThisSize > 0) {
        bagBreakdown.push({
          variantId: bag.variantId,
          weightKg: bag.weightKg,
          count: bagsOfThisSize
        });
        remainingKg -= bagsOfThisSize * bag.weightKg;
      }
    }
    
    // If there's still remaining kg needed, add one more bag of the largest size
    if (remainingKg > 0 && sortedBags.length > 0) {
      const largestBag = sortedBags[0];
      const existingEntry = bagBreakdown.find(item => item.variantId === largestBag.variantId);
      if (existingEntry) {
        existingEntry.count += 1;
      } else {
        bagBreakdown.push({
          variantId: largestBag.variantId,
          weightKg: largestBag.weightKg,
          count: 1
        });
      }
    }
    
    const requiredBags = bagBreakdown.reduce((sum, item) => sum + item.count, 0);
    const totalKg = bagBreakdown.reduce((sum, item) => sum + (item.weightKg * item.count), 0);

    // Store bag breakdown for add to cart
    this.bagBreakdown = bagBreakdown;

    // Format bag breakdown: "(count x weightKg, count x weightKg)"
    const bagBreakdownText = bagBreakdown.length > 0
      ? `(${bagBreakdown.map(item => `${item.count} x ${this.#formatNumber(item.weightKg)}kg`).join(', ')})`
      : '';

    //console.log(bagBreakdown);

    // show the result and hide error if present
    resultError.classList.add('hidden');
    result.classList.remove('hidden');
    
    // Update add to cart button text with pack count
    const addToCartButton = resultAddtocart.querySelector('[data-calculator-add-to-cart] span');
    if (addToCartButton && this.addToCartLabel) {
      const addToCartText = this.addToCartLabel;
      addToCartButton.textContent = `${addToCartText} - ${requiredBags} Pack${requiredBags>1 ? 's' : ''}`;
    }

    const unitLabel = unit
      .replace('sqm', 'sq m')
      .replace('sqyard', 'sq yard')
      .replace('sqfeet', 'sq feet');
      resultCalculation.innerHTML = `
        <div class="">You will require the following amount of <strong>${this.productTitle}</strong></div>
        <div class="bg-light-green p-4 text-center my-4">
          <div class="text-base"><strong>Coverage Area</strong>: ${this.#formatNumber(areaVal)} ${unitLabel}</div>
          <div class="text-base"><strong>Lawn type</strong>: ${type === 'new' ? 'New lawn' : 'Overseed lawn'}</div>
          <div class="text-base"><strong>Amount required</strong>: ${this.#formatNumber(totalKg)} kg</div>
          <div class="text-base"><strong>Packs needed</strong>: ${requiredBags}<br />${bagBreakdownText}</div>
        </div>
        <div class="text-xs text-gray-600 hidden">Assumptions: New lawn ${this.NEW_LAWN_KG_PER_1000_SQM} g/m², Overseed ${this.OVERSEED_KG_PER_1000_SQM} g/m², Bag ${this.BAG_WEIGHT_KG} kg.</div>
    `;
  }

  /**
   * Handles adding multiple variants to cart
   */
  #handleAddToCart = async (event) => {
    event.preventDefault();
    
    if (!this.bagBreakdown || this.bagBreakdown.length === 0) {
      console.error('No bag breakdown available');
      return;
    }

    const button = event.target.closest('[data-calculator-add-to-cart]');
    const buttonText = button?.querySelector('span');
    const originalText = buttonText?.textContent || 'Add to Cart';
    
    if (button) {
      button.disabled = true;
      if (buttonText) buttonText.textContent = 'Adding...';
    }

    // Build updates object: { variantId: quantity, ... }
    const updates = {};
    this.bagBreakdown.forEach(item => {
      updates[item.variantId] = item.count;
    });

    try {
      const response = await fetch(Theme.routes.cart_update_url, fetchConfig('json', {
        body: JSON.stringify({ updates })
      }));

      if (!response.ok) {
        throw new Error('Failed to add items to cart');
      }

      const cart = await response.json();

      // Dispatch cart update event
      this.dispatchEvent(
        new CartUpdateEvent(
          {},
          this.id,
          {
            source: 'product-calculator',
            itemCount: cart.item_count,
            preventCartDrawer: false
          }
        )
      );

      // Update button state
      if (button) {
        button.disabled = false;
        if (buttonText) buttonText.textContent = 'Added to Cart';
        setTimeout(() => {
          if (buttonText) buttonText.textContent = originalText;
        }, 2000);
      }

    } catch (error) {
      console.error('Error adding to cart:', error);
      this.dispatchEvent(
        new CartErrorEvent(
          this.id,
          error.message || 'Failed to add items to cart'
        )
      );

      if (button) {
        button.disabled = false;
        if (buttonText) buttonText.textContent = 'Try Again';
        setTimeout(() => {
          if (buttonText) buttonText.textContent = originalText;
        }, 2000);
      }
    }
  };
}

if (!customElements.get('product-calculator-component')) {
  customElements.define('product-calculator-component', ProductCalculatorComponent);
}
